/**
 * La hoja de papel y cuánto cabe en ella. **Común a todo lo que se imprime.**
 *
 * ⚠ **Vivía en `rotulacion/medidas.ts` y no es de la rotulación.** Un carnet se
 * recorta de la misma hoja carta que una etiqueta y con el mismo margen; dejar aquí
 * la medida del papel es lo que impide que el segundo módulo que imprima se invente
 * la suya. Lo que sí es de cada módulo es cuánto mide SU pieza.
 */

/** El papel. Carta, que es lo que hay en la oficina y en la impresora. */
export const HOJA = { ancho: 216, alto: 279 } as const

/**
 * Margen de la hoja. 10 mm y no menos: por debajo de eso muchas láser entran en su
 * zona no imprimible y recortan el borde del marco justo por donde se corta.
 */
export const MARGEN_HOJA = 10

/**
 * El hueco entre dos piezas de la misma hoja, en mm.
 *
 * ⚠ **Tiene que coincidir con el `gap` de `.pliego-hoja` en `globales.css`.** Está
 * declarado en los dos sitios porque uno es maquetación y el otro es aritmética, y
 * no hay forma de que el CSS le pase su `gap` a una función de TypeScript. Si se
 * cambia uno, se cambia el otro: el precio de que no coincidan es una hoja que dice
 * llevar tres piezas y saca dos.
 */
export const SEPARACION_PLIEGO = 4

/**
 * Cuántas piezas caben en una hoja carta, dada su medida.
 *
 * ⚠ **Cuenta con el margen, no sólo con el papel.** Sin restarlo, una etiqueta de
 * 105 mm de alto dice que caben dos en 279 mm — y la segunda sale cortada por abajo,
 * que es un pliego entero a la basura.
 */
export function cabenPorHoja(ancho: number, alto: number): number {
  const util = { ancho: HOJA.ancho - MARGEN_HOJA * 2, alto: HOJA.alto - MARGEN_HOJA * 2 }
  // ⚠ **N piezas llevan N−1 huecos, y por eso se suma uno a los dos lados de la
  // división.** Sin contar el hueco, la cuenta decía que dos etiquetas de 128 mm
  // caben en 259 —256 de papel, sí— y no caben: con los 4 mm de separación son 260
  // y la segunda se iba a la hoja siguiente. El pliego salía con la mitad de las
  // hojas a medio llenar y nadie sabía por qué.
  const encajan = (util: number, pieza: number) =>
    Math.max(1, Math.floor((util + SEPARACION_PLIEGO) / (pieza + SEPARACION_PLIEGO)))
  return encajan(util.ancho, ancho) * encajan(util.alto, alto)
}
